// Per-language default UI texts (iteration 26): the language resolution + the merge every
// widget's pack module (./dropdown.ts, ./results.ts, ./modal.ts, ./chatbot.ts,
// ./assistant-modal.ts) uses.
//
// Import-free and pure (no SDK value imports, `.ts` relative imports in this directory), so
// the packs and their merge run under `pnpm test` (Node type stripping) like ../helpers/*.

// The languages a pack exists for. Anything else (and a missing / non-string locale) → "en".
export const TEXT_LANGUAGES = ["cs", "sk", "en", "de"] as const;
export type TextLanguage = (typeof TEXT_LANGUAGES)[number];

// One pack per language, every pack with the SAME keys (test/texts.test.ts guards it).
export type TextPacks<T> = { readonly [L in TextLanguage]: Readonly<T> };

// The pack language of a widget config's `locale`: its language subtag, case-insensitive
// ("cs-CZ" → cs, "sk_SK" → sk, "EN" → en, "de-AT" → de). Unknown or missing → "en".
export function textLanguage(locale: unknown): TextLanguage {
  if (typeof locale !== "string") return "en";
  const language = locale.trim().split(/[-_]/)[0]?.toLowerCase() ?? "";
  return (TEXT_LANGUAGES as readonly string[]).includes(language) ? (language as TextLanguage) : "en";
}

// `target` with every key of `pack` that is `undefined` on it (absent, or explicitly undefined)
// set from the pack. A value the config sets wins, an empty string included. Never mutates:
// returns a shallow copy when something was filled, else `target` itself (a complete config
// stays the very same object).
export function fillTexts<C extends object>(target: C, pack: object): C {
  let out: Record<string, unknown> | undefined;
  for (const [key, value] of Object.entries(pack)) {
    if ((target as Record<string, unknown>)[key] === undefined) {
      out ??= { ...(target as Record<string, unknown>) };
      out[key] = value;
    }
  }
  return (out ?? target) as C;
}

export const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
